import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatUnixDateTime } from '@/shared/lib/datetime';
import { getListRows } from '@/shared/lib/pagination';
import { QueryStatus } from '@/shared/ui/QueryStatus';
import { AssignOperatorModal } from './AssignOperatorModal';
import { fetchOperators, unassignOperator } from './api';

interface OperatorRow extends Record<string, unknown> {
  id: number | string;
  name?: string;
  email?: string;
  phone?: string;
  lastlog?: { mark_type?: number | string; mark_name?: string; mark_time?: number } | null;
}

const MARK_LABEL_CLASS: Record<number, string> = {
  0: 'label-default',
  1: 'label-success',
  2: 'label-warning',
};

const OPERATORS_KEY = ['callcenter-operators'];

export const CallCenterPage = () => {
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data, isLoading, error } = useQuery({ queryKey: OPERATORS_KEY, queryFn: fetchOperators });

  const unassign = useMutation({
    mutationFn: unassignOperator,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: OPERATORS_KEY }),
  });

  const rows = getListRows<OperatorRow>(data, ['items']);

  return (
    <>
      <section className="content-header">
        <h1>Коллцентр</h1>
        <button type="button" className="btn btn-info pull-right" onClick={() => setIsModalOpen(true)}>
          Прикрепить
        </button>
      </section>
      <section className="content">
        <div className="box">
          <div className="box-header with-border">
            <h3 className="box-title">Список прикрепленных сотрудников</h3>
          </div>
          <div className="box-body">
            <QueryStatus isLoading={isLoading} error={error} errorText="Ошибка загрузки списка сотрудников" />
            {unassign.isError && (
              <div className="alert alert-danger">{unassign.error.message || 'Ошибка открепления сотрудника'}</div>
            )}
            <table className="table table-bordered">
              <thead>
                <tr>
                  <th>id</th>
                  <th>Имя</th>
                  <th>Email</th>
                  <th>Телефон</th>
                  <th>Статус</th>
                  <th>Время изменения статуса</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map(({ id, name, email, phone, lastlog }) => (
                  <tr key={id}>
                    <td>{id}</td>
                    <td>{name}</td>
                    <td>{email}</td>
                    <td>{phone}</td>
                    <td>
                      {lastlog ? (
                        <span className={`label ${MARK_LABEL_CLASS[Number(lastlog.mark_type)] ?? 'label-default'}`}>
                          {lastlog.mark_name}
                        </span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td>{lastlog ? formatUnixDateTime(lastlog.mark_time) : '-'}</td>
                    <td className="text-center">
                      <button
                        type="button"
                        className="btn btn-danger"
                        disabled={unassign.isPending}
                        onClick={() => unassign.mutate(id)}
                      >
                        <i className="fa fa-trash-o" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
      {isModalOpen && <AssignOperatorModal onClose={() => setIsModalOpen(false)} />}
    </>
  );
};
